import { forwardRef, useState } from 'react';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PropTypes from 'prop-types';

import IconButton from '@zenbusiness/zui/IconButton';
import InputAdornment from '@zenbusiness/zui/InputAdornment';
import TextField from '@zenbusiness/zui/TextField';

/**
 * PasswordField Component
 *
 * A TextField component with a toggle to show/hide password visibility.
 * Fully compatible with react-hook-form and all TextField props.
 *
 * @param {object} props - All TextField props are supported
 * @param {object} ref - Forwarded ref for react-hook-form
 * @returns {React.ReactElement} The PasswordField component
 */
const PasswordField = forwardRef(({ InputProps, ...props }, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleToggleVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const mergedInputProps = {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          edge="end"
          label={showPassword ? 'Hide password' : 'Show password'}
          onClick={handleToggleVisibility}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
    ...InputProps
  };

  return (
    <TextField
      {...props}
      ref={ref}
      InputProps={mergedInputProps}
      type={showPassword ? 'text' : 'password'}
    />
  );
});

PasswordField.displayName = 'PasswordField';

PasswordField.propTypes = {
  /** Input props for the underlying TextField (merged with visibility toggle) */
  InputProps: PropTypes.object
};

export default PasswordField;
